import React from 'react';
import {
  Button,
  Grid,
  TextFieldProps,
  TextField,
  Typography,
} from '@material-ui/core';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import LoadingInputEndAdornment from 'components/atoms/LoadingInputEndAdornment';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import { useQuery } from 'react-query';
import { getCluster } from 'services/cluster';
import { INSTALLATION_TYPE } from 'constants/checklist';
import { postAddInstallation } from 'services/installation';
import ActionsButtonsBaseModal from 'components/atoms/ActionsButtonsBaseModal';
import ConfirmModal from 'components/atoms/ConfirmModal';
import { IModalAddInstallationProps, IOption } from './types';
import useStyles from './style';

const NO_OPTIONS_TEXT = 'Nenhum resultado encontrado';
const DEFAULT_HELPER_TEXT = 'Obrigatório';
const DEFAULT_OK_TEXT = 'Obrigatório';
const FORM_ID = 'ADD_INSTALLATION_REGISTER_EQUIPMENT';
const DEFAULT_ERROR_TEXT = 'Texto ultrapassou o limite de 5 caracteres';

function ModalAddInstallation({
  children,
  onClose,
  open,
  reloadData,
}: IModalAddInstallationProps) {
  const classes = useStyles();
  const [loadingSaveForm, setLoadingSaveForm] = React.useState<boolean>(false);

  const [openConfirmModal, setOpenConfirmModal] = React.useState(false);
  const [clusterOptions, setClusterOptions] = React.useState<IOption[]>([]);

  const filter = createFilterOptions<IOption>();
  const [typeValue, setTypeValue] = React.useState<IOption | null>(null);
  const installationsTypeOptions = Object.values(INSTALLATION_TYPE).map(
    item => ({
      label: item,
      value: item,
    }),
  ) as IOption[];

  const validationSchema = yup.object({
    cluster: yup.string().required(DEFAULT_OK_TEXT),
    name: yup.string().required(DEFAULT_OK_TEXT),
    installationType: yup.string().required(DEFAULT_OK_TEXT),
    abbreviation: yup
      .string()
      .max(5, DEFAULT_ERROR_TEXT)
      .required(DEFAULT_OK_TEXT),
  });

  const formik = useFormik({
    initialValues: {
      cluster: '',
      name: '',
      abbreviation: '',
      installationType: '',
    },
    validationSchema,
    onSubmit: formValues => {
      const formattedvalues = {
        ...formValues,
        abbreviation: formValues.abbreviation?.toUpperCase(),
      };

      // se for enviado com sucesso
      setLoadingSaveForm(true);
      postAddInstallation(formattedvalues)
        .then(() => {
          // solicita uma recarga de dados
          if (reloadData) reloadData();
          // fecha o formulário
          onClose();
        })
        .finally(() => setLoadingSaveForm(false));
    },
  });

  const onCloseModal = React.useCallback(() => {
    if (formik.dirty) {
      setOpenConfirmModal(true);
    } else {
      onClose();
    }
  }, [formik, onClose]);

  const handleAutoCompleteChange = React.useCallback(
    (name: string, option: IOption) => {
      const value = option?.value || '';
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const getOptionSelected = (option: IOption, anotherOption: IOption) =>
    option.value === anotherOption.value;

  const inputRenderer = React.useCallback(
    (params: TextFieldProps, loading?: boolean) => (
      <TextField
        {...params}
        className={classes.formBaseInput}
        variant="outlined"
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              <LoadingInputEndAdornment loading={loading} />
              {params?.InputProps?.endAdornment}
            </>
          ),
        }}
      />
    ),
    [classes.formBaseInput],
  );

  const enableQueries = React.useMemo(() => open, [open]);

  const enableClusterOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const queryClusterOptions = useQuery({
    queryKey: ['clusterOptionsContext'],
    queryFn: async () => {
      const { data } = await getCluster();

      return data;
    },
    select: data => {
      return data.map(item => {
        return {
          label: item?.description,
          value: item?.description,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setClusterOptions(data);
    },
    cacheTime: 0,
    enabled: enableClusterOptions,
  });

  React.useEffect(() => {
    // se existir alteracoes no campo & estiver com a modal fechada
    if (!open) {
      if (formik.dirty || !formik.isValid) {
        // então: reset form quando eu fecho o modal,
        formik.resetForm();
        // caso esteja fechado e existir valores nos campos listas eles devem ser limpos
        if (clusterOptions.length) setClusterOptions([]);
        if (typeValue) setTypeValue(null);
      }
    }
  }, [clusterOptions.length, formik, open, typeValue]);

  const renderBody = React.useCallback(
    () => (
      <form id={FORM_ID} onSubmit={formik.handleSubmit}>
        <Grid container className={classes.bodyContent} spacing={0}>
          <Grid item xs={12}>
            <Typography className={classes.formMessage}>
              Preencha os dados Obrigatórios:
            </Typography>
          </Grid>

          <Grid container direction="row" alignItems="center" spacing={2}>
            <Grid item xs={6}>
              <Autocomplete
                id="cluster"
                className={classes.inputList}
                options={clusterOptions}
                onChange={(e, selected) => {
                  return handleAutoCompleteChange(
                    'cluster',
                    selected as IOption,
                  );
                }}
                onBlur={formik.handleBlur}
                getOptionLabel={option => String(option.label)}
                getOptionSelected={getOptionSelected}
                noOptionsText={NO_OPTIONS_TEXT}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);

                  if (params.inputValue.length >= 2) {
                    filtered.push({
                      label: params.inputValue,
                      value: params.inputValue,
                    });
                  }

                  return filtered;
                }}
                renderInput={params => {
                  const extraProps = {
                    name: 'cluster',
                    label: 'Cluster',
                    error:
                      formik.touched.cluster && Boolean(formik.errors.cluster),
                    helperText:
                      (formik.touched.cluster && formik.errors.cluster) ||
                      DEFAULT_HELPER_TEXT,
                  };
                  return inputRenderer(
                    { ...params, ...extraProps },
                    queryClusterOptions?.isLoading,
                  );
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                id="installationType"
                className={classes.inputList}
                options={installationsTypeOptions}
                getOptionLabel={option => String(option.label)}
                value={typeValue}
                onChange={(e, selected) => {
                  setTypeValue(selected);
                  handleAutoCompleteChange(
                    'installationType',
                    selected as IOption,
                  );
                }}
                onBlur={formik.handleBlur}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Tipo instalaçâo"
                    variant="outlined"
                    error={
                      formik.touched.installationType &&
                      Boolean(formik.errors.installationType)
                    }
                    helperText={
                      (formik.touched.installationType &&
                        formik.errors.installationType) ||
                      DEFAULT_HELPER_TEXT
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="name"
                label="Nome da Instalaçâo"
                variant="outlined"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={
                  (formik.touched.name && formik.errors.name) ||
                  DEFAULT_HELPER_TEXT
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="abbreviation"
                label="Sigla"
                variant="outlined"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.abbreviation &&
                  Boolean(formik.errors.abbreviation)
                }
                helperText={
                  (formik.touched.abbreviation && formik.errors.abbreviation) ||
                  DEFAULT_HELPER_TEXT
                }
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
          </Grid>
        </Grid>
      </form>
    ),
    [
      classes.bodyContent,
      classes.formMessage,
      classes.inputList,
      clusterOptions,
      filter,
      formik.errors.abbreviation,
      formik.errors.cluster,
      formik.errors.installationType,
      formik.errors.name,
      formik.handleBlur,
      formik.handleChange,
      formik.handleSubmit,
      formik.touched.abbreviation,
      formik.touched.cluster,
      formik.touched.installationType,
      formik.touched.name,
      handleAutoCompleteChange,
      inputRenderer,
      installationsTypeOptions,
      queryClusterOptions?.isLoading,
      typeValue,
    ],
  );

  const renderFooter = React.useCallback(() => {
    return (
      <div className={classes.footer}>
        <Button color="primary" onClick={onCloseModal}>
          Cancelar
        </Button>
        <ButtonWithLoading
          form={FORM_ID}
          disabled={!formik.dirty || !formik.isValid}
          type="submit"
          color="primary"
          isLoading={loadingSaveForm}
        >
          Salvar
        </ButtonWithLoading>
      </div>
    );
  }, [
    classes.footer,
    formik.dirty,
    formik.isValid,
    loadingSaveForm,
    onCloseModal,
  ]);

  return (
    <>
      <ConfirmModal
        title="Confirmar Saída"
        message="É possível que as alterações feitas não sejam salvas."
        open={openConfirmModal}
        width="433px"
        onAccept={() => {
          onClose();
          setOpenConfirmModal(false);
        }}
        onDecline={() => setOpenConfirmModal(false)}
      />
      <ActionsButtonsBaseModal
        title="Cadastrar Instalação"
        body={renderBody()}
        footer={renderFooter()}
        open={open}
        onClose={onCloseModal}
        width="629px"
      >
        {children}
      </ActionsButtonsBaseModal>
    </>
  );
}

export default ModalAddInstallation;
